// The mode light in a shipped game (§32 L1): the faction's own need.
//
// The tuner lights chess or factory on a seeded schedule (`trainerMode`). A
// shipped game has no trainer, so the driver asks this instead — factory by
// default, chess while a Monarch is threatened — and writes the bit onto the
// **planner's copy** of the state, never the store's. The reducer never sees
// it, the log never carries it, the hash never moves; the plan is still a pure
// function of the state the driver handed over, so a replay replays it.

import { fileOf, pieceColor, rankOf, type Side } from "src/engine/rules/board";
import { isAttackedByEnemyOf } from "src/engine/rules/chess/attacks";
import type { GameState } from "src/engine/rules/commands";
import { areEnemies, clonePosition, royalsOf, type Position } from "src/engine/rules/position";
import { modeBit, SIGNAL_MODE_MASK } from "src/engine/rules/trainer";

/** How near an enemy piece may come to a Monarch before the army fights. */
export const THREAT_RADIUS = 2;

/**
 * Chess while any Monarch of `side` is attacked (check included) or an enemy
 * piece stands within `THREAT_RADIUS` of it (Chebyshev); factory otherwise.
 */
export function threatMode(position: Position, side: Side): "chess" | "factory" {
  const royals = royalsOf(position, side);
  for (const king of royals) {
    if (isAttackedByEnemyOf(position, king, side)) return "chess";
  }
  for (const [sq, piece] of position.board) {
    if (!areEnemies(position, side, pieceColor(piece))) continue;
    for (const king of royals) {
      const away = Math.max(
        Math.abs(fileOf(sq) - fileOf(king)),
        Math.abs(rankOf(sq) - rankOf(king))
      );
      if (away <= THREAT_RADIUS) return "chess";
    }
  }
  return "factory";
}

/**
 * `state` with `side`'s mode bit set, for the planner alone.
 *
 * **A copy**: the position is cloned (`clonePosition`, once a turn) so the
 * live store's Maps are never shared with a planner that might write them.
 * **A light already lit wins** — a board carrying either mode bit (the
 * tuner's) is returned as it is.
 */
export function withModeLight(state: GameState, side: Side): GameState {
  if (state.position.signals & SIGNAL_MODE_MASK) return state;
  const position = clonePosition(state.position);
  position.signals |= modeBit(threatMode(state.position, side));
  return { ...state, position };
}
